/* eslint-disable no-console */
import {FC, useState, useRef, useEffect, useContext} from "react";
import {deleteModule, initFlatList} from "./flatArrayUtils/flatArrayUtils";
import NewModuleModal from "./newModuleModal";
import {INotificationSeverity, NotificationContext, useApi} from "@buildwitt/shapeshift-identity";
import {IModuleBase} from "@buildwitt/shapeshift-types/ICourse";
import {corePaths, ICourseCurriculumItemEntity, ICourseEntity, IFlatList} from "@buildwitt/shapeshift-types";
import {makeStyles} from "tss-react/mui";
import {Box, Button, Chip, Typography} from "@mui/material";
import {ICurriculumItemImport, ModulesMap} from "@/types/curriculum";
import {IconAdminCourses} from "@/icons/IconAdminCourses";
import {Add, Delete} from "@mui/icons-material";
import {DataGridPro, GridColumns, GridToolbarContainer, GridToolbarFilterButton} from "@mui/x-data-grid-pro";
import {deepCopy} from "@buildwitt/shapeshift-common/v1/common";

const MAX_CURRICULUM_ITEMS = 20;

export type IProps = {
	progressionControl?: boolean,
	deepLinks?: boolean,
	onChange: (e: IFlatList[]) => void,
} & {initialData?: ICourseEntity};

const useStyles = makeStyles()((theme) => ({
	container: {
		height: "1000px"
	},
	label: {
		fontWeight: 600,
		fontSize: "0.75rem"
	},
	moduleContainer: {
	},
	toolbar: {
		"button": {
			border: "0px solid"
		}
	},
	dataGrid: {},
	actionControl: {},
	chooseFromLibBtn: {
		"svg path": {
			fill: "black"
		},
		"svg": {
			width: "18px"
		}
	},
	createNewBtn: {
		color: theme.palette.primary.main,
		"&:hover": {
			color: theme.palette.primary.contrastText
		}

	},
	modulesAddedFooterStat: {
		borderTop: "1px solid " + theme.palette.divider, //> #393939
		color: "#FFFFFFB2",
		fontSize: "12px",
		padding: theme.spacing(2)
	}
})
);

declare global {
	interface Array<T> {
		move(from: number, to: number): Array<T>;
	}
}

const AdminCourseCurriculum: FC<IProps> = ({onChange, initialData: courseData}) => {
	const {classes} = useStyles();
	const [newModuleModalOpen, setNewModuleModalOpen] = useState<{ mode: "new" | "import", lessonId?: number } | false>(false)
	const [flatList, setFlatList] = useState<IFlatList[]>([]);
	const [flatListHistoryStack, setFlatListHistoryStack] = useState<IFlatList[][]>([]);
	const [courseModules, getCourseModulesApi] = useApi<IModuleBase[]>();
	const [courseCurriculumItems, getCourseCurriculumItemsApi] = useApi<ICourseCurriculumItemEntity[]>();
	const [modulesMap, setModulesMap] = useState<ModulesMap | undefined>(courseData?.id ? undefined : {});
	const {notify} = useContext(NotificationContext);

	const headCells: GridColumns<IFlatList> = ([
		{
			field: "title",
			flex: 1,
			headerName: "Title",
			minWidth: 200,
			type: "string",
			renderCell: (e) => {
				return (
					<Box
						sx={{cursor: "pointer", "&:hover": {textDecoration: "underline"}}}
						onClick={() => {
							if (e.row.type === corePaths.lesson) {
								setNewModuleModalOpen({mode: "new", lessonId: e.row.moduleId});
							}
						}}
					>
						{e.row.title}
					</Box>
				);
			}
		},
		{
			width: 1,
			field: "_isDraft",
			flex: 1,
			headerName: "",
			sortable: false,
			filterable: false,
			maxWidth: 75,
			minWidth: 75,
			renderCell: (e) => {
				const isDraft = !!(e.row.moduleId && !modulesMap?.[e.row.type]?.[e.row.moduleId]?.publishedAt);
				if(isDraft)
					return <Chip
						variant="outlined"
						color="primary"
						label={"Draft"}
						sx={(theme) => ({
							"& .MuiChip-label": {
								color: theme.palette.primary.main
							}
						})}
					/>
				return <></>;
			}
		},
		{
			width: 1,
			field: "_delete",
			flex: 1,
			headerName: "",
			sortable: false,
			filterable: false,
			maxWidth: 50,
			minWidth: 50,
			renderCell: (e) => {
				return <Delete onClick={() => handleDelete(e.row.dragId)}/>
			}
		}
	])

	useEffect(() => {
		if(courseData?.id){
			getCourseModulesApi("/api/course/" + courseData?.id + "/modules");
			getCourseCurriculumItemsApi("/api/course/" + courseData?.id + "/curriculum/items")
		}
		else
			setFlatList([])
	}, [courseData?.id])


	const firstRun = useRef(true);
	useEffect(() => {
		if(!courseCurriculumItems.data || firstRun.current == false)
			return;

		initList()
		firstRun.current = false;
	}, [courseCurriculumItems.data]);


	const initList = (): void => {
		if(!courseCurriculumItems.data)
			return;

		setFlatList(initFlatList(courseCurriculumItems.data))
		setFlatListHistoryStack([]);
	}

	useEffect(() => {
		const isUpdate = (): boolean => {
			return flatListHistoryStack.length > 0;
		}

		if (!isUpdate()) return;

		onChange(flatList);
	}, [flatList, flatListHistoryStack.length])

	useEffect(() => {
		if (courseModules.data) {
			const map: ModulesMap = {};
			courseModules.data.forEach(e => {
				if(!e.__type)
					return;

				if(!map[e.__type])
					map[e.__type] = {}

				map[e.__type][e.id] = e;
			});
			setModulesMap(map);
		}
	}, [courseModules]);


	const addToStack = (state: IFlatList[]): void => {
		setFlatListHistoryStack(prev => {
			return [...prev, deepCopy(state)]
		});
	}

	const handleNewModulesModalClose = (newModules?: ICurriculumItemImport[]): void => {
		setNewModuleModalOpen(false);

		if (!newModules || !modulesMap) return;

		// Separate existing items (updates) from new items (additions)
		const existingItems: ICurriculumItemImport[] = [];
		const newItems: ICurriculumItemImport[] = [];

		newModules.forEach(e => {
			if(!e.type) return;
			const existingIndex = flatList.findIndex(item => item.moduleId === e.id && item.type === e.type);
			if (existingIndex >= 0) {
				existingItems.push(e);
			} else {
				newItems.push(e);
			}
		});

		// Check space for new items (existing items don't count against the limit)
		const freeSpaces = MAX_CURRICULUM_ITEMS - flatList.length;

		// If we can't add all new items, trim and notify
		if (newItems.length > freeSpaces) {
			if (freeSpaces <= 0) {
				// Can't add any items - notify and return early
				notify(`There is on remaining space in the course. You can only add ${MAX_CURRICULUM_ITEMS} to the course.`,"", INotificationSeverity.warning, 10000);
				return;
			}
			// Can add some but not all - trim to fit and notify
			newItems.splice(freeSpaces);
			notify(`You can only add ${MAX_CURRICULUM_ITEMS} to the course. Additional lessons were not added.`,"", INotificationSeverity.warning, 10000);
		}

		const deepClonedFlatList = deepCopy(flatList);
		const newMap = {...modulesMap};

		// Update map for all items (both existing and new)
		// This ensures the map has the latest data for both updates and additions
		const allItems = [...existingItems, ...newItems];
		allItems.forEach(e => {
			if(!e.type) return;

			if(!newMap[e.type])
				newMap[e.type] = {}

			// Update the module in the map
			newMap[e.type][e.id] = {
				__type: e.type,
				complete: false,
				id: e.id,
				slug: e.slug,
				content: "",
				subtitle: "",
				title: e.title,
				publishedAt: e.publishedAt
			};
		});

		// Build items to add directly from newItems (already separated and trimmed)
		const itemsToAdd: IFlatList[] = newItems.map(e => ({
			type: e.type!,
			slug: e.slug,
			title: e.title,
			level: 0,
			dragId: e.type + "-" + e.id,
			moduleId: e.id,
			progression: true
		}));

		// Update the flat list with both new items and updates to existing items
		if (itemsToAdd.length > 0 || existingItems.length > 0) {
			setFlatList(prev => {
				const updated = [...prev];

				// Update existing items (we already know these exist from the separation step)
				existingItems.forEach(e => {
					if(!e.type) return;
					const existingIndex = updated.findIndex(item => item.moduleId === e.id && item.type === e.type);
					if (existingIndex >= 0) {
						updated[existingIndex] = {
							...updated[existingIndex],
							title: e.title,
							slug: e.slug
						};
					}
				});

				// Add new items
				if (itemsToAdd.length > 0) {
					return [...updated, ...itemsToAdd];
				}

				return updated;
			});
			addToStack(deepClonedFlatList);
		}

		//update map with new additions/updates
		setModulesMap(newMap)
	}

	const handleDelete = (dragId: string): void => {
		const index = flatList.findIndex(e => e.dragId === dragId);
		const deepClonedFlatList = deepCopy(flatList);

		setFlatList(prev => deleteModule(prev, index))
		addToStack(deepClonedFlatList);
	}

	return (
		<>
			{!!(flatList.length) && <DataGridPro<IFlatList>
				className={classes.dataGrid}
				autoHeight
				getRowId={(row) => row.dragId}
				aria-label="admin table"
				pagination={false}
				rowCount={flatList?.length ?? 0}
				rows={flatList ?? []}
				filterMode="client"
				onRowOrderChange={(e) => {
					const {oldIndex, targetIndex} = e;
					const deepClonedFlatList = deepCopy(flatList);
					setFlatList((prev) => {
						const updated = [...prev];
						const [movedItem] = updated.splice(oldIndex, 1); // remove item
						updated.splice(targetIndex, 0, movedItem); // insert at new index
						return updated;
					});
					addToStack(deepClonedFlatList);
				}}
				rowReordering
				columns={headCells}
				components={{
					Toolbar: () => <GridToolbarContainer><GridToolbarFilterButton {...({} as any)} /> {/* crazy ts issue with mui 5 typing - cast here to squash */}</GridToolbarContainer>,
					Footer: () => <Box className={classes.modulesAddedFooterStat}><Typography variant="caption">
						{flatList.length}/{MAX_CURRICULUM_ITEMS} ADDED
					</Typography></Box>}}
			/>}

			<Box display={"flex"} gap={2} onClick={() => ((MAX_CURRICULUM_ITEMS - flatList.length) <= 0) && notify("Max lesson count has already been reached", "", INotificationSeverity.warning, 10000, false)}>
				<Button disabled={(MAX_CURRICULUM_ITEMS - flatList.length) <= 0} variant="contained" className={classes.chooseFromLibBtn} onClick={() => setNewModuleModalOpen({mode: "import"})}>
					<IconAdminCourses/> CHOOSE FROM LIBRARY
				</Button>
				<Button disabled={(MAX_CURRICULUM_ITEMS - flatList.length) <= 0} variant="outlined" className={classes.createNewBtn} onClick={() => setNewModuleModalOpen({mode: "new"})}>
					<Add/> CREATE NEW
				</Button>
			</Box>
			{newModuleModalOpen && <NewModuleModal
				mode={newModuleModalOpen.mode}
				lessonId={newModuleModalOpen.lessonId}
				typeFilter={corePaths.lesson}
				currentModules={flatList}
				onClose={(newModules) => {
					handleNewModulesModalClose(newModules);
					setNewModuleModalOpen(false);
				}}
				onDeleteResponse={(response) => {
					// When lesson is deleted, remove it from curriculum and close modal
					// Use the same logic as the trash can icon delete
					if (response.statusCode === 200 && !response.isLoading && newModuleModalOpen.lessonId) {
						const dragId = corePaths.lesson + "-" + newModuleModalOpen.lessonId;
						setFlatList(prev => {
							const deepClonedFlatList = deepCopy(prev);
							const index = prev.findIndex(e => e.dragId === dragId);
							if (index >= 0) {
								addToStack(deepClonedFlatList);
								return deleteModule(prev, index);
							} else {
								// Lesson was deleted but not found in curriculum - may have already been removed
								notify("Lesson was deleted but was not found in the course curriculum.", "Lesson Not Found", INotificationSeverity.warning, 5000);
							}
							return prev;
						});
						setNewModuleModalOpen(false);
					}
				}}
			/>}
		</>
	)
}

export default AdminCourseCurriculum;